import { getCurrentStoreConfig } from "@app/config";
import { Fetcher } from "./fetch";

// Add near other type definitions
type EventSubmissionBody = {
  action: string;
  data: {
    points?: number;
    [key: string]: any;
  };
};

export class CloudFetcher extends Fetcher {
  generateAppUrl(endpoint: string) {
    const config = getCurrentStoreConfig();
    return `${config.shopify.url}${config.shopify.appSubpath}${endpoint}`;
  }

  async get<Return>(_endpoint: string, params?: URLSearchParams) {
    const hasParams = params && params.size > 0;
    const endpoint = hasParams ? `${_endpoint}?${params.toString()}` : _endpoint;

    const init = {
      method: "GET",
    };

    const url = this.generateAppUrl(endpoint);
    const response = await fetch(url, init);
    if (response.ok) return (await response.json()) as Return;
    else this.error(response);
  }

  async post<Return, Body>(endpoint: string, body: Body) {
    const init = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: typeof body === "string" ? body : JSON.stringify(body),
    };

    const url = this.generateAppUrl(endpoint);
    const response = await fetch(url, init);
    if (response.ok) return (await response.json()) as Return;
    else this.error(response);
  }

  async updateCustomerDetails(details: any) {
    return this.post("/customer/update", {
      body: JSON.stringify(details),
    });
  }

  async fetchCustomerRewards() {
    const response = await this.get<{ data: any }>("/customer/loyalty/rewards");
    if (!response) throw new Error("No data found in response");
    return response;
  }

  async fetchCustomerClaimedRewards() {
    const response = await this.get<{ data: any }>("/customer/loyalty/rewards/claimed");
    if (!response) throw new Error("No data found in response");
    return response;
  }

  async fetchCustomerRedeemableRewards() {
    const response = await this.get<{ data: any }>("/customer/loyalty/rewards/redeemable");
    if (!response) throw new Error("No data found in response");
    return response;
  }

  async fetchCustomerRedeemedRewards() {
    const response = await this.get<{ data: any }>("/customer/loyalty/rewards/redeemed");
    if (!response) throw new Error("No data found in response");
    return response;
  }

  async fetchCustomerProfile() {
    const response = await this.get<{ data: any }>("/customer/loyalty/profile");

    if (!response?.data) throw new Error("No data found in response");

    return response.data;
  }

  async fetchCustomerChallenges() {
    const response = await this.get<{ payload: any }>("/customer/loyalty/challenges");
    if (!response?.payload) throw new Error("No data found in response");
    return response.payload;
  }

  async fetchCustomerHistory() {
    const response = await this.get<{ data: any }>("/customer/loyalty/history");

    if (!response?.data) throw new Error("No data found in response");

    return response.data;
  }

  async fetchCustomerWallet() {
    const response = await this.get<{ data: any }>("/customer/loyalty/wallet");
    if (!response?.data) throw new Error("No data found in response");

    return response.data;
  }

  async fetchCustomerTiers() {
    const response = await this.get<{ data: any }>("/customer/loyalty/tiers");
    if (!response?.data) throw new Error("No data found in response");
    return response.data;
  }

  async submitEvent(action: string, data: { points?: number; [key: string]: any }) {
    return this.post<{ data: any }, EventSubmissionBody>("/customer/loyalty/events", {
      action,
      data,
    });
  }

  async fetchNostoGalleryContent() {
    const endpoint = this.generateAppUrl("/nosto/gallery");
    return this.get(endpoint);
  }
}

export const cloud = new CloudFetcher();
